import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { getUserPlan } from "../../utils/planAccess";
import { formatRegionPrice, getRegion } from "../../utils/regions";

interface PromoCopy {
  eyebrow: string;
  headline: string;
  body: string;
  subtext: string;
  cta: string;
}

interface EnergyMatchPromo {
  contextKey: string | null;
  copy: PromoCopy;
  benefitsPreview: string[];
  pricing: { showForFreeUsers: boolean; displayMode: string; placement: string };
}

// Energy Match™ upsell at the bottom of Life GPS. Free users only; the server
// picks the copy from the user's relationship score and recent questions and
// returns null for paid plans, so a stale local user can't show it to Pro.
const EnergyMatchPromoCard: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isFree = getUserPlan(user) === "free";
  const [promo, setPromo] = useState<EnergyMatchPromo | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");

  useEffect(() => {
    if (!user || !isFree) {
      setPromo(null);
      return;
    }
    let cancelled = false;
    api
      .get("/backend/life-gps/promo/energy-match")
      .then((res) => {
        if (!cancelled) setPromo(res.data?.data ?? null);
      })
      .catch(() => {
        // A promo is optional: on failure simply don't show it.
      });
    return () => {
      cancelled = true;
    };
  }, [user, isFree]);

  if (!promo) return null;

  // The server prices the checkout (the regional amount shown on this card),
  // so the browser sends nothing and just follows the Stripe URL it gets back.
  const handleUnlock = async () => {
    setCheckoutError("");
    setRedirecting(true);
    try {
      const res = await api.post("/payment/energy-match/checkout");
      if (res?.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      setCheckoutError(t("astriaHome.journey.stripeMissing"));
    } catch (err: any) {
      setCheckoutError(
        err?.response?.data?.message ||
          err?.message ||
          t("astriaHome.journey.checkoutFailed"),
      );
    }
    setRedirecting(false);
  };

  const { copy, benefitsPreview, pricing } = promo;
  const region = getRegion(user?.region || "healjai");
  const price =
    pricing.showForFreeUsers && region
      ? `${formatRegionPrice(region, region.monthlyPrice)}${t("astriaHome.journey.perMonthShort", "/mo")}`
      : null;

  return (
    <motion.section
      className="mx-auto mt-8 w-full max-w-3xl"
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      aria-label={copy.eyebrow}
    >
      <div
        className="relative overflow-hidden rounded-[28px] px-6 py-7 sm:px-8 sm:py-8"
        style={{
          background: "var(--astria-surface)",
          border: "1px solid rgba(167,139,255,0.28)",
          boxShadow: "0 18px 40px rgba(0,0,0,0.35)",
        }}
      >
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.2em]"
          style={{
            backgroundColor: "var(--astria-accent-glow)",
            color: "var(--astria-text-primary)",
          }}
        >
          <Sparkles className="h-3 w-3 text-astria-accent" aria-hidden="true" />
          {copy.eyebrow}
        </span>

        <h2 className="mt-4 text-2xl font-semibold leading-tight text-astria-text sm:text-3xl">
          {copy.headline}
        </h2>
        <p className="mt-2 text-[15px] leading-relaxed text-astria-text">{copy.body}</p>
        <p className="mt-1.5 text-[13px] italic leading-relaxed text-astria-muted">
          {copy.subtext}
        </p>

        <ul className="mt-5 flex flex-wrap gap-2">
          {benefitsPreview.map((benefit) => (
            <li
              key={benefit}
              className="rounded-full px-3 py-1 text-[12px] text-astria-text"
              style={{ border: "1px solid rgba(167,139,255,0.25)" }}
            >
              {benefit}
            </li>
          ))}
        </ul>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <button
            type="button"
            onClick={handleUnlock}
            disabled={redirecting}
            className="inline-flex w-full disabled:opacity-60 items-center justify-center rounded-full px-7 py-3 text-[15px] font-bold transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#a78bff] sm:w-auto"
            style={{
              backgroundColor: "var(--astria-accent-primary)",
              color: "var(--astria-bg-app)",
              boxShadow: "var(--astria-shadow-button)",
            }}
          >
            {copy.cta}
          </button>
          {checkoutError && (
            <p role="alert" className="text-[12px] text-red-400">
              {checkoutError}
            </p>
          )}
          {price && (
            <span className="text-right text-xs text-astria-muted">{price}</span>
          )}
        </div>
      </div>
    </motion.section>
  );
};

export default EnergyMatchPromoCard;
